import { type Theme } from "@mui/material";

/**
 * Bottom padding to apply *inside* a page's scroll container so the last
 * scrollable content clears the floating Menu / Tabs pill on mobile.
 *
 * Mirrors the formula used by `LegacyRouteWrapper` for the legacy mobile
 * pages: `floatingNavigation.height + floatingNavigation.margin + spacing(3)`
 * (the extra `spacing(3)` is breathing room above the pill).
 *
 * Returns `undefined` when the theme does not define `size.floatingNavigation`
 * (e.g. test themes / non-Berlin themes), matching the `LegacyRouteWrapper`
 * guard so callers don't accidentally pad an environment where no floating
 * pill exists.
 *
 * Apply this on the element that actually scrolls — never on the page wrapper
 * itself, which has `overflow: hidden` and would just shrink the visible page
 * area, leaving a static empty footer behind the floating pill. Components
 * that render on both mobile and desktop must additionally scope this value
 * to a mobile breakpoint (e.g. `theme.breakpoints.down("md")`); the floating
 * Menu pill only renders inside `AppLayoutMobile`, so applying the padding
 * unconditionally would leave dead space at the bottom of desktop pages.
 */
export function getFloatingNavigationScrollPaddingBottom(theme: Theme): string | undefined {
  if (!theme.size?.floatingNavigation) {
    return undefined;
  }
  const { height, margin } = theme.size.floatingNavigation;
  return `calc(${height} + ${margin} + ${theme.spacing(3)} + env(safe-area-inset-bottom))`;
}
